import React, { FC } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import TableTab from "../../../components/tabs/table-tab";
import { FILTER_COMPANIES, FILTER_CURRENT, FILTER_DEPARTMENTS, FILTER_SITES, FILTER_TAGHOLDERS, FILTER_INACTIVE_FOR } from "../../../constants/filter-types";
import { FILTER_INACTIVE_DAYS_90 } from "../../../constants/element-type";
import { REPORTS_CARDS_NOT_USED } from "../../../constants/menu-types";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import CardsNotUsedReportColumns from "../../../models/ColumnDefinitions/CardsNotUsedReportColumns";
import { Filter } from "../../../models/Filter";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import CardsNotUsedReportSearch from "../../../models/search/CardsNotUsedReportSearch";
import CompanySearch from "../../../models/search/CompanySearch";
import DepartmentSearch from "../../../models/search/DepartmentSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import { auditRunReport } from "../../../services/auditService";
import { getCompanyNamesWithPagination } from "../../../services/companyService";
import { getDepartmentNamesWithPagination } from "../../../services/departmentService";
import { getCardsNotUsedReport } from "../../../services/reportService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { getEnrolmentNamesWithPagination } from "../../../services/enrolmentService";
import { getUserFiltersByView } from "../../../services/userPreferenceService";
import { getErrorMessage, showError } from "../../../services/utilities/ServiceError";
import EnrolmentSearch from "@/models/search/EnrolmentSearch";

interface CardsNotUsedReportProps {
    reportId: string;
}

const CardsNotUsedReport: FC<CardsNotUsedReportProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const userLoginId = useSelector((state: any) => (state.userInfo.id));
    const currentMenuDetail = REPORTS_CARDS_NOT_USED;
    const lookup: Lookup[] = [
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: "companyId",
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "departmentId",
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "masterId",
            values: [],
            dataMethod: getEnrolmentNamesWithPagination,
            searchAccessor: EnrolmentSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: "includeNonCurrent",
            values: [
                { value: "true", label: translate.byKey(TranslationKeys.yes) },
                { value: "false", label: translate.byKey(TranslationKeys.no) }
            ],
            isSingleSelect: true
        },
    ];

    const getOriginalStaticFilter = async (filterKey: string) => {
        switch (filterKey) {
            case "includeNonCurrent":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    clearPolicy: "locked",
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.includeNonCurrent),
                    options: [
                        { label: translate.byKey(TranslationKeys.yes), value: "true" },
                        { label: translate.byKey(TranslationKeys.no), value: "false" },
                    ],
                    selected: [{ label: translate.byKey(TranslationKeys.yes), value: "true" }],
                };
            case "inactiveFor":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    clearPolicy: "locked",
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.inactiveFor),
                    options: [],
                    selected: [{ elementSysname: FILTER_INACTIVE_DAYS_90, label: translate.byKey(TranslationKeys.inactiveFor90Days), value: FILTER_INACTIVE_DAYS_90 }],
                };
            case "site.id":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.site),
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
            case "masterId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.displayName),
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
            case "companyId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.company),
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
            case "departmentId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.department),
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
            default:
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: filterKey,
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
        }
    };

     const initialiseUserPreferences = async (setDetails: (userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys) => void) => {
          const staticFilterKeys = ["site.id", "companyId", "departmentId", "masterId", "inactiveFor", "includeNonCurrent"];
          let customFilter: Filter[] = [];

          try {
              const savedFilters = await getUserFiltersByView(userLoginId, currentMenuDetail, reportId);

              if (!savedFilters.find(f => f.filterId === FILTER_INACTIVE_FOR)) {
                  customFilter.push(await getOriginalStaticFilter("inactiveFor"));
              }
              if (!savedFilters.find(f => f.filterId === FILTER_SITES)) {
                  customFilter.push(await getOriginalStaticFilter("site.id"));
              }
              if (!savedFilters.find(f => f.filterId === FILTER_COMPANIES)) {
                  customFilter.push(await getOriginalStaticFilter("companyId"));
              }
              if (!savedFilters.find(f => f.filterId === FILTER_DEPARTMENTS)) {
                  customFilter.push(await getOriginalStaticFilter("departmentId"));
              }
              if (!savedFilters.find(f => f.filterId === FILTER_TAGHOLDERS)) {
                  customFilter.push(await getOriginalStaticFilter("masterId"));
              }
              if (!savedFilters.find(f => f.filterId === FILTER_CURRENT)) {
                  customFilter.push(await getOriginalStaticFilter("includeNonCurrent"));
              }
          } catch (error) {
              showError(error, toast.warn);
          } finally {
              setDetails(userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys);
          }
      };

     const getDataMethod = async (listParameters?: ListQuery) => {
          try {
              await auditRunReport(reportId);
              return getCardsNotUsedReport(listParameters);
          } catch (error) {
              return Promise.reject(getErrorMessage(error));
          }
      }

      return <TableContextProvider
          dataMethod={getDataMethod}
          initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
          tableDescriptor={CardsNotUsedReportColumns}
          searchDescriptor={CardsNotUsedReportSearch}
          useFilter
          useSort
          usePagination
          useSearch
          useTable
          lookup={lookup}
          loadDataOnDemand
          runDataOnDemandButtonLabel={translate.byKey(TranslationKeys.runReport)}
          getOriginalStaticFilter={getOriginalStaticFilter}>
          <TableTab
              title={''}
              reportTitle={translate.byKey(TranslationKeys.cardsNotUsed)}
              selectRows={false}
              showFilterMenu={true}  
              cyName="cardsNotUsedReport"/>
      </TableContextProvider>
  };

export default CardsNotUsedReport;

